/**
 * "People also ask" pages on easement width, depth and removal.
 *
 * Each page answers one sentence-style query directly, then points the reader
 * at three county record hubs that fit that question (recorder search plus the
 * county's published GIS layers). Hub notes describe what the hub gives you for
 * this question; they never state a county rule we have not verified.
 */
import type { HomeownerQuestion } from "./homeowner-questions";

export const PAA_QUESTIONS: HomeownerQuestion[] = [
  // ---------------------------------------------------------------- WIDTH
  {
    slug: "how-wide-is-a-drainage-easement",
    seoTitle: "How Wide Is a Drainage Easement? What Sets the Feet",
    seoDescription:
      "Drainage easement width follows the channel or pipe plus room for crews to maintain it. Learn what sets the feet and where to read your recorded width.",
    question: "How wide is a drainage easement?",
    shortAnswer:
      "There is no single width. A drainage easement covers the swale, ditch or pipe plus the strip crews need to reach it, so the number on your recorded plat or grant controls, and open channels run wider than buried pipe.",
    answer: [
      "A drainage easement is sized to the water it has to carry and the machinery that has to maintain it. A shallow swale between two lots can sit in a narrow strip. A deep open ditch or a detention channel needs room for its banks, a maintenance track beside it, and a place to stack cleaned-out material.",
      "Buried storm pipe is usually the narrowest case, because the strip only has to give a crew room to excavate along the pipe. Open channels and ponds are the widest, since the easement has to include the whole water surface and its slopes.",
      "Many local governments set minimum widths in their subdivision or stormwater design standards, and those minimums apply to new plats. They do not rewrite an older recorded easement. The width that binds your lot is the dimension on the plat, the recorded grant, or the drainage plan recorded with the subdivision.",
    ],
    whatToLookFor: [
      "A dimension in feet on the recorded plat, often written beside the lot line it follows.",
      "Whether the strip is measured from the lot line, the center of the pipe, or the top of the channel bank.",
      "Wording such as \"drainage and maintenance\" or \"access\", which shows crews may drive along the strip.",
      "Notes about a pond, detention area or overflow path that widen the strip at one spot.",
    ],
    relatedGlossary: ["utility-easement", "servient-estate", "encroachment", "buildable-envelope"],
    relatedQuestions: [
      "what-is-a-drainage-easement",
      "how-wide-is-a-utility-easement",
      "can-i-build-a-fence-across-an-easement",
    ],
    category: "Types of easements",
    countyHubs: [
      {
        hubId: "va-fairfax",
        note: "Publishes a stormwater easement layer, so you can see how a large county draws drainage strips around real lots before you pull the recorded plat.",
      },
      {
        hubId: "fl-sarasota",
        note: "Publishes an easement layer alongside its recorder search, useful for comparing a mapped drainage strip with the dimension on the recorded document.",
      },
      {
        hubId: "fl-leon",
        note: "Carries a large published easement layer plus recorder access, a quick way to spot a drainage strip and then open its source document.",
      },
    ],
  },
  {
    slug: "how-wide-is-a-driveway-easement",
    seoTitle: "How Wide Is a Driveway Easement? Typical Feet and Limits",
    seoDescription:
      "Shared driveway and access easements are often 12 to 20 feet wide, but the recorded document sets yours. See what controls width and how to find it.",
    question: "How wide is a driveway easement?",
    shortAnswer:
      "Many shared driveway and access easements run 12 to 20 feet, but the recorded grant controls. If it states no width, courts generally allow what was reasonably needed for the use when the easement was created.",
    answer: [
      "A driveway or access easement exists so one owner can reach their land across another's. The width is whatever the grant says, and drafters commonly pick a figure that fits one lane of traffic with a little shoulder, in the range of twelve to twenty feet. Where a lot is reached by a long private lane, a wider strip may have been written in so two vehicles can pass or emergency equipment can turn in.",
      "Many local fire codes are built on the International Fire Code, which calls for a clear width of twenty feet on roads fire apparatus must use. Each jurisdiction adopts and amends that code on its own, so a long private drive that serves several homes may face a width requirement that a single-home drive does not.",
      "When an older easement gives a right of way but no dimension, the question becomes what the parties reasonably intended and what the use needs. That is a fact question, and disputes over it often end up in court. The practical fix is a recorded amendment or survey that fixes the centerline and the width in writing.",
    ],
    whatToLookFor: [
      "The width in feet, and whether it is measured from a centerline or from a boundary.",
      "A surveyed exhibit attached to the grant, which usually pins the strip more tightly than a text description.",
      "Wording about maintenance, snow removal and cost sharing, which often travels with the width.",
      "Whether the grant allows utilities to be laid under the same strip.",
    ],
    relatedGlossary: ["appurtenant-easement", "dominant-estate", "servient-estate", "right-of-way"],
    relatedQuestions: [
      "can-my-neighbor-use-my-driveway",
      "who-maintains-a-shared-driveway",
      "can-a-neighbor-block-my-easement",
    ],
    category: "Driveways & access",
    countyHubs: [
      {
        hubId: "va-chesterfield",
        note: "Publishes an ingress and egress easement layer, a layer aimed squarely at access strips like shared driveways.",
      },
      {
        hubId: "mn-ramsey",
        note: "Offers a published easement layer plus recorder search, handy for checking a mapped access strip against the recorded grant.",
      },
      {
        hubId: "tx-travis",
        note: "Combines easement lines, parcel maps and recorder search in one hub, so you can go from a mapped strip to the instrument that names its width.",
      },
    ],
  },
  {
    slug: "is-a-utility-easement-measured-from-the-property-line",
    seoTitle: "Is a Utility Easement Measured From the Property Line?",
    seoDescription:
      "A utility easement may run along a lot line, split across two lots, or center on a pipe. See how the plat describes it and how to measure yours.",
    question: "Is a utility easement measured from the property line?",
    shortAnswer:
      "Often, but not always. Plat easements commonly run along a lot line, sometimes split between neighbors, while pipeline and power easements are usually centered on the facility. The recorded wording tells you which.",
    answer: [
      "Subdivision plats usually draw utility easements against the lot lines. A note such as \"10 foot utility easement along rear lot lines\" means the strip is measured inward from that boundary. On many plats the strip along a shared side line is split, with a few feet on each lot adding up to one corridor for the utility.",
      "Easements granted for a specific line or pipe work differently. They are often described as a set number of feet on each side of a centerline, which means the strip moves with the facility rather than with your fence or property line.",
      "The pipe or pole you can see may not sit where the paper says. Lines are sometimes installed off center, and old descriptions can be vague. When it matters, such as before a build, have a surveyor stake the easement from the recorded description instead of measuring from the visible equipment.",
    ],
    whatToLookFor: [
      "The phrase \"along\", \"adjacent to\" or \"centered on\" in the plat note or grant.",
      "A dimension labeled on each side of a shared lot line, which shows a split strip.",
      "A recorded survey exhibit that ties the strip to monuments or a plat bearing.",
      "Any difference between the described location and where the facility actually sits.",
    ],
    relatedGlossary: ["utility-easement", "survey", "setback", "buildable-envelope"],
    relatedQuestions: [
      "how-wide-is-a-utility-easement",
      "do-i-need-a-survey-to-locate-an-easement",
      "can-i-build-a-shed-over-an-easement",
    ],
    category: "Utility requests",
    countyHubs: [
      {
        hubId: "fl-pinellas",
        note: "Publishes a utility easement layer, so you can see how strips sit against lot lines in a built-out county before opening the plat.",
      },
      {
        hubId: "az-maricopa",
        note: "Offers easement lines and parcel GIS next to recorder search, a good place to compare a mapped strip with the plat note that describes it.",
      },
      {
        hubId: "il-dupage",
        note: "Combines a published right-of-way and easement layer with recorder search for checking how a described strip lines up with the lot.",
      },
    ],
  },

  // ---------------------------------------------------------------- DEPTH
  {
    slug: "how-deep-are-utility-lines-buried-in-an-easement",
    seoTitle: "How Deep Are Utility Lines Buried in an Easement?",
    seoDescription:
      "Gas mains need at least 24 inches of cover and service lines 12 to 18 inches under federal rules. Other lines vary. See why you must never guess depth.",
    question: "How deep are utility lines buried in an easement?",
    shortAnswer:
      "There is no one depth. Federal rules set minimum cover for gas pipe, 24 inches over mains and 12 inches over service lines on private property, while water, sewer, electric and telecom depth depends on the utility, soil and frost. Never assume.",
    answer: [
      "Federal pipeline safety rules do set floors for natural gas. A buried gas main needs at least 24 inches of cover under 49 CFR 192.327, with 30 to 36 inches for transmission lines depending on the area. A service line to a house needs at least 12 inches on private property and 18 inches under streets and roads under 49 CFR 192.361. Those are minimums, and lines are often deeper.",
      "For other utilities there is no national number. Water lines are usually set below the local frost line, so they run deeper in cold climates. Sewer lines depend on grade and flow. Electric and communications lines follow each utility's own standards and local code, and they can be much shallower than people expect.",
      "Depth also changes over time. Erosion, regrading, a new driveway or a past repair can leave a line closer to the surface than the day it went in. The easement document almost never states a depth, so the only safe approach is to have the lines located and exposed by hand before any digging.",
    ],
    whatToLookFor: [
      "Any depth or cover language in the recorded grant, which is rare but worth checking.",
      "The utility's own as-built drawings, which some operators release on request.",
      "Locate marks from 811, which show where a line runs, not how deep it is.",
      "Signs of past regrading or trenching near the strip that could have changed the cover.",
    ],
    relatedGlossary: ["utility-easement", "servient-estate", "encroachment", "easement"],
    relatedQuestions: [
      "how-deep-can-i-dig-in-a-utility-easement",
      "how-wide-is-a-utility-easement",
      "can-i-plant-trees-in-a-utility-easement",
    ],
    category: "Utility requests",
    countyHubs: [
      {
        hubId: "tx-travis",
        note: "Lists a utilities layer next to easement lines and parcel maps, a starting point for seeing which corridors cross your lot before you call for locates.",
      },
      {
        hubId: "az-maricopa",
        note: "Has utility and easement layers with recorder search, so you can match a mapped corridor to the instrument that created it.",
      },
      {
        hubId: "fl-pinellas",
        note: "Publishes a utility easement layer, useful for confirming that a strip exists before you ask the operator about its facilities.",
      },
    ],
  },
  {
    slug: "how-deep-can-i-dig-in-a-utility-easement",
    seoTitle: "How Deep Can I Dig in a Utility Easement? Call 811 First",
    seoDescription:
      "Before digging in a utility easement, call 811 and hand dig near the marks. Depth limits come from your grant and the utility. Here is what to check first.",
    question: "How deep can I dig in a utility easement?",
    shortAnswer:
      "The easement rarely sets a depth limit, but you still cannot dig freely. Call 811 first, hand dig near the marks, and get the utility's written permission for anything permanent such as footings or deep posts.",
    answer: [
      "Owning the land under an easement does not give you a free hand inside the strip. You keep title, but the holder has a right to use the corridor, and damaging its line can leave you paying for repairs and, in some cases, facing fines. Shallow gardening is usually fine. Digging for a footing, a pond, a retaining wall or deep fence posts is where trouble starts.",
      "State one-call laws require you to request locates before excavating, and the call is free. Most states also set a tolerance zone on each side of the marks, often a foot or two, where you must expose the line by hand. Those zones differ by state, so check the rule where you live.",
      "Marks show the line's path, not its depth. Because a line may be shallower than expected, even a small hole near a mark should be dug carefully. If your plan puts anything permanent in the strip, ask the utility in writing first. Many publish an encroachment policy that lists what they allow.",
    ],
    whatToLookFor: [
      "Restrictions in the recorded grant on structures, grading or changes in elevation.",
      "The utility's published encroachment or right-of-way policy.",
      "Your state's one-call rules, including the tolerance zone and how long marks stay valid.",
      "Marked lines that run closer to your planned hole than you expected.",
    ],
    relatedGlossary: ["utility-easement", "encroachment", "servient-estate", "right-of-way"],
    relatedQuestions: [
      "how-deep-are-utility-lines-buried-in-an-easement",
      "what-happens-if-i-build-on-an-easement",
      "can-i-build-a-fence-across-an-easement",
    ],
    category: "Building & fences",
    countyHubs: [
      {
        hubId: "nd-dunn",
        note: "Has a utilities layer and section line right-of-way data beside recorder search, a good example of how rural corridors cross private land.",
      },
      {
        hubId: "az-maricopa",
        note: "Combines utility and easement layers with recorder search, so you can see what is mapped near your dig site before you call for locates.",
      },
      {
        hubId: "sc-beaufort",
        note: "Lists road right-of-way and utility data along with parcel and tax maps, handy for checking which corridors touch your lot.",
      },
    ],
  },
  {
    slug: "how-far-down-does-an-easement-go",
    seoTitle: "How Far Down Does an Easement Go? Depth and Scope",
    seoDescription:
      "An easement covers what its grant allows, not unlimited depth. Learn how scope limits underground use, what stays yours, and where to read the grant.",
    question: "How far down does an easement go?",
    shortAnswer:
      "An easement reaches only as deep as the grant and its purpose require. A pipe easement covers the space the pipe and its upkeep need, and you keep the rest of the ground unless the document says otherwise.",
    answer: [
      "An easement is a limited right to use land for a stated purpose, not ownership of the land. A utility easement for a buried line lets the holder place and service that line. It does not hand over an unlimited column of earth below your lot.",
      "Courts read the scope from the document, and when it is silent they usually allow what is reasonably necessary for the purpose. Because of that, how far down an easement goes depends on what was granted and what the use actually requires, not on a fixed depth.",
      "Minerals are a separate question. An ordinary utility easement does not transfer oil, gas or other minerals, which are owned and leased on their own terms. Check the grant for language about the subsurface, and check your title for any separate mineral reservation.",
    ],
    whatToLookFor: [
      "Words describing the purpose, such as \"to lay, maintain and replace a pipeline\".",
      "Any mention of \"subsurface\", \"underground\" or \"minerals\" in the grant.",
      "Overhead language, since lines also use the space above, with clearance rules.",
      "A separate recorded mineral reservation that is unrelated to the surface easement.",
    ],
    relatedGlossary: ["easement", "servient-estate", "utility-easement", "encumbrance"],
    relatedQuestions: [
      "who-owns-the-land-under-an-easement",
      "how-deep-are-utility-lines-buried-in-an-easement",
      "what-is-an-easement-on-my-property",
    ],
    category: "Types of easements",
    countyHubs: [
      {
        hubId: "il-cook",
        note: "Links to the county recorder and parcel maps, where you can pull the grant and read exactly what it allows below and above the ground.",
      },
      {
        hubId: "wa-king",
        note: "Offers recorder search and a parcel service with a large record count, a fast route to the instrument that defines an easement's scope.",
      },
      {
        hubId: "ca-los-angeles",
        note: "Provides recorder access and parcel maps useful for pulling the original grant.",
      },
    ],
  },

  // -------------------------------------------------------------- REMOVAL
  {
    slug: "how-do-i-vacate-a-utility-easement",
    seoTitle: "How Do I Vacate a Utility Easement? Steps and Who Signs",
    seoDescription:
      "To vacate a utility easement, petition the local government and get each utility's consent, then record the order. Here are the steps and what to expect.",
    question: "How do I vacate a utility easement?",
    shortAnswer:
      "For an easement shown on a plat, you generally petition the city or county to vacate it, with written consent from every utility using it, and then record the approval. A private grant needs a signed release from its holder.",
    answer: [
      "The route depends on how the easement was created. One drawn on a recorded subdivision plat is usually removed through a local vacation or abandonment process run by the planning, public works or clerk's office. One created by a separate recorded grant to a utility ends when the utility signs a release.",
      "In most places the process looks the same. You file a petition, the city or county circulates it to every utility that has facilities in or near the strip, and each one either consents, objects, or asks to keep the easement. Then there is usually public notice and a hearing. If the utility still has a line there, expect it to refuse or to require relocation at your cost.",
      "Approval alone changes nothing on your title. The vacation order or release must be recorded with the county recorder. Fees, timelines and required forms are set locally, so start by asking the jurisdiction for its checklist.",
    ],
    whatToLookFor: [
      "Whether the easement came from a plat or a separate recorded grant.",
      "Which utilities have lines in or near the strip, including ones you cannot see.",
      "The local petition form, notice rules and fee schedule.",
      "A recorded order or release that cites the original instrument number.",
    ],
    relatedGlossary: ["utility-easement", "encumbrance", "title-policy-exception", "easement"],
    relatedQuestions: [
      "can-i-remove-an-easement-from-my-property",
      "who-has-to-sign-to-release-an-easement",
      "can-an-easement-be-moved",
    ],
    category: "Disputes & neighbors",
    countyHubs: [
      {
        hubId: "il-cook",
        note: "Opens the county recorder search where a recorded vacation order or release would be filed and later found.",
      },
      {
        hubId: "tx-harris",
        note: "Connects to the county clerk's records and a large parcel service, useful for confirming the original instrument before you petition.",
      },
      {
        hubId: "fl-miami-dade",
        note: "Includes a published platting service next to recorder access, a place to check the plat that created a strip you want vacated.",
      },
    ],
  },
  {
    slug: "who-has-to-sign-to-release-an-easement",
    seoTitle: "Who Has to Sign to Release an Easement? Holder and Lender",
    seoDescription:
      "A release is signed by the easement holder, and every owner of a benefited parcel if it is appurtenant. Learn who signs and how it gets recorded.",
    question: "Who has to sign to release an easement?",
    shortAnswer:
      "The holder of the easement signs the release, because only the person who holds the right can give it up. For an easement serving a neighboring lot, that means every owner of that lot, and sometimes its lender.",
    answer: [
      "A release is a document in which the holder gives up the easement. The person who benefits from the right has to be the one to sign it, not only the person whose land it crosses. For a utility easement, that is the utility, usually through its real estate or right-of-way department.",
      "For an appurtenant easement, which serves a neighboring parcel, the benefit belongs to that parcel and passes with it. Every current owner of the benefited parcel should sign. If that parcel carries a mortgage, ask the title company whether the lender must also consent, since the easement is part of what secures the loan.",
      "The signed release then has to be notarized to the standard your state requires, and recorded with the county recorder, ideally with a legal description of the easement and the recording information of the original grant. Until it is recorded, the title record still shows the easement.",
    ],
    whatToLookFor: [
      "The exact name of the grantee in the original recorded easement.",
      "Whether the easement is appurtenant, which points to the neighboring parcel's owners.",
      "A mortgage on the benefited parcel that could require lender sign-off.",
      "Notary and recording requirements at your county recorder.",
    ],
    relatedGlossary: ["appurtenant-easement", "dominant-estate", "servient-estate", "easement-in-gross"],
    relatedQuestions: [
      "can-i-remove-an-easement-from-my-property",
      "how-do-i-vacate-a-utility-easement",
      "can-an-easement-expire",
    ],
    category: "Disputes & neighbors",
    countyHubs: [
      {
        hubId: "mn-hennepin",
        note: "Provides county recorder access and parcel maps, a way to find the original grant and identify the holder who must sign.",
      },
      {
        hubId: "oh-franklin",
        note: "Offers recorder search and parcel GIS, useful for finding the benefited parcel and its current owner.",
      },
      {
        hubId: "ga-fulton",
        note: "Combines recorder access with parcel maps, so you can trace an easement to the lot it serves.",
      },
    ],
  },
  {
    slug: "how-do-i-find-out-if-an-easement-was-ever-released",
    seoTitle: "How Do I Find Out If an Easement Was Ever Released?",
    seoDescription:
      "Search the county recorder index under the holder's name for a release, termination or vacation of the easement. Here is how to check and confirm it.",
    question: "How do I find out if an easement was ever released?",
    shortAnswer:
      "Search the county recorder's index for a release, termination or vacation tied to the original instrument. A title company can confirm in writing whether the exception on your title commitment is still live.",
    answer: [
      "A release only counts once it is recorded, so the answer is in the county records. Start with the original easement and note its book and page or instrument number. Then search the recorder's grantor and grantee index for later documents that cite it, using the names of the holder and every owner since.",
      "Look for titles such as release of easement, termination of easement, vacation order or quitclaim. Releases are often indexed under the holder's name, so searching only under your own name can miss them. Later deeds that say property is \"subject to\" an easement do not prove it still exists, since the language is often copied forward.",
      "If your title commitment lists the easement as an exception, ask the title company to confirm in writing whether it is still valid. If they find a recorded release, they can often remove the exception or insure over it. If nothing is recorded, assume the easement stands.",
    ],
    whatToLookFor: [
      "The original instrument number to use as the search key.",
      "Later recordings under the holder's name that use the words release, termination or vacation.",
      "A plat replat or vacation that dropped a strip shown on the earlier plat.",
      "A written title company statement about the Schedule B exception.",
    ],
    relatedGlossary: ["schedule-b", "title-commitment", "title-policy-exception", "encumbrance"],
    relatedQuestions: [
      "how-to-read-a-title-commitment-for-easements",
      "can-an-easement-expire",
      "how-do-i-find-out-who-owns-an-easement",
    ],
    category: "Finding easements",
    countyHubs: [
      {
        hubId: "wa-king",
        note: "Links to the county recorder search and a large parcel service, where grantor and grantee indexes help you find a release.",
      },
      {
        hubId: "nc-mecklenburg",
        note: "Connects to the register of deeds and parcel GIS, a starting point for searching under the holder's name.",
      },
      {
        hubId: "pa-allegheny",
        note: "Offers recorder access and a county parcel service, useful for tracing a plan or deed back to the original grant.",
      },
    ],
  },
];
